import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { POSITION_SLOTS, markerFor, getStartingLineup, PLAY_COORDINATE_VERSION } from "../lib/engine";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import SquadManager from "../components/SquadManager";
import { DeletePlayDialog } from "../components/DeletePlayDialog";
import Whiteboard from "../components/Whiteboard";
import { CRESTS } from "../lib/crests";
import { toast } from "sonner";
import { ArrowLeft, Plus, Clapperboard, Trash2, Copy, ListChecks, Clock, Presentation } from "lucide-react";

const CATEGORIES = ["ALL", "DEFENCE", "KICKOUTS", "ATTACK", "COUNTERATTACK", "TRANSITION", "PRESS", "RESTARTS", "SET PLAYS"];
const CATEGORY_LABELS = { ALL: "All plays", DEFENCE: "Defence", KICKOUTS: "Kick-outs", ATTACK: "Attack", COUNTERATTACK: "Counterattacks", TRANSITION: "Transitions", PRESS: "Pressing", RESTARTS: "Restarts", "SET PLAYS": "Set plays", OTHER: "Other plays" };

function buildEntities(team) {
  const lineup = getStartingLineup(team);
  const list = [];
  POSITION_SLOTS.forEach((slot, i) => {
    const p = lineup[i] || {};
    const pos = i + 1; // starters wear their POSITION number (1..15) on the pitch
    list.push({ id: `A${pos}`, team: "A", number: pos, name: p.name || "", position: slot.label, role: p.role || slot.label, x: slot.x, y: slot.y });
  });
  // opposition mirrored so #(17-n) marks our #n (jerseys add to 17); both keepers stay in goal
  for (let m = 1; m <= 15; m++) {
    const ownSlot = POSITION_SLOTS[m - 1];
    let x, y;
    if (m === 1) { x = 45; y = 139; }
    else { const si = 16 - m; const mk = markerFor(POSITION_SLOTS[si], si); x = mk.x; y = mk.y; }
    list.push({ id: `B${m}`, team: "B", number: m, name: "Opp", position: ownSlot.label, role: ownSlot.label, x, y });
  }
  return list;
}

export default function Playbook() {
  const { teamId } = useParams();
  const nav = useNavigate();
  const [team, setTeam] = useState(null);
  const [plays, setPlays] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [openNew, setOpenNew] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [newPlay, setNewPlay] = useState({ name: "", category: "ATTACK" });

  const load = useCallback(() => {
    setLoadError(null);
    Promise.all([api.get(`/teams/${teamId}`), api.get(`/plays?teamId=${teamId}`)])
      .then(([teamResponse, playsResponse]) => { setTeam(teamResponse.data); setPlays(playsResponse.data); })
      .catch((error) => setLoadError([403, 404].includes(error.response?.status) ? "This team is unavailable or you do not have access to it." : apiError(error)));
  }, [teamId]);
  useEffect(load, [load]);

  const createPlay = async () => {
    if (!team) return;
    const entities = buildEntities(team);
    const payload = {
      name: newPlay.name || "New Play",
      description: "",
      teamId,
      sport: team.sport,
      category: newPlay.category,
      duration: 4,
      pitchType: "full",
      coordinateVersion: PLAY_COORDINATE_VERSION,
      entities,
      ball: { holderId: "A1", x: 45, y: 6 },
      events: [],
      stages: [{ id: "s1", name: "Stage 1", duration: 4 }],
      annotations: [],
      notes: [],
    };
    try {
      const { data } = await api.post("/plays", payload);
      toast.success("Play created");
      nav(`/studio/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const del = (play, e) => { e.stopPropagation(); setPendingDelete(play); };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try { await api.delete(`/plays/${pendingDelete.id}`); setPlays((p) => p.filter((x) => x.id !== pendingDelete.id)); toast.success(`"${pendingDelete.name}" deleted`); setPendingDelete(null); }
    catch (err) { toast.error(apiError(err)); }
    finally { setDeleting(false); }
  };

  const duplicatePlay = async (play, event) => {
    event.stopPropagation();
    try {
      const { data: source } = await api.get(`/plays/${play.id}`);
      const { id, created_at, updated_at, createdBy, ...copy } = source;
      const { data } = await api.post("/plays", { ...copy, sourcePack: null, scenarioCode: null, packRevision: null, name: `${source.name} (copy)` });
      toast.success("Editable copy created"); nav(`/studio/${data.id}`);
    } catch (error) { toast.error(apiError(error)); }
  };

  const shown = plays.filter((p) => (filter === "ALL" || p.category === filter) && `${p.name} ${p.description}`.toLowerCase().includes(search.trim().toLowerCase()));
  const groups = [...new Set([...CATEGORIES.slice(1), ...shown.map((p) => p.category || "OTHER")])]
    .map((category) => ({ category, plays: shown.filter((p) => (p.category || "OTHER") === category) }))
    .filter((group) => group.plays.length);

  if (loadError) return (
    <main className="min-h-screen bg-background px-5 py-12 max-w-2xl mx-auto">
      <h1 className="font-display text-2xl font-bold">Unable to open this playbook</h1>
      <p data-testid="playbook-load-error" role="alert" className="text-muted-foreground mt-3">{loadError}</p>
      <div className="flex gap-3 mt-6">
        <Button data-testid="playbook-error-back" onClick={() => nav("/teams")}>Back to my teams</Button>
        <Button data-testid="playbook-error-retry" variant="secondary" onClick={load}>Try again</Button>
      </div>
    </main>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 z-30 bg-background/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button data-testid="back-to-teams" variant="ghost" size="sm" onClick={() => nav("/teams")}><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <div className="font-display font-bold leading-tight flex items-center gap-2">{team && (() => { const C = CRESTS[team.logo] || CRESTS.shield; return <C className="h-4 w-4" style={{ color: team.colorPrimary || "#0EA5E9" }} />; })()}{team?.name || "…"}</div>
              <div className="text-[11px] text-muted-foreground font-mono2 flex items-center gap-1">
                <Copy className="h-3 w-3" />{team?.code} · {team?.sport}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          {team && <SquadManager team={team} onSaved={setTeam} />}
          {team && <Button data-testid="team-board-btn" variant="secondary" onClick={() => setBoardOpen(true)} className="text-[#F59E0B] hover:text-[#F59E0B]"><Presentation className="h-4 w-4 mr-1" />Board</Button>}
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-play-btn"><Plus className="h-4 w-4 mr-1" />New play</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle className="font-display">New play</DialogTitle><DialogDescription>Name your play and pick a category. It opens with your Starting 15 on the pitch.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="new-play-name" placeholder="Play name (e.g. Kickout to Midfield)" value={newPlay.name} onChange={(e) => setNewPlay({ ...newPlay, name: e.target.value })} />
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Category</div>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.slice(1).map((c) => (
                      <button key={c} data-testid={`new-play-category-${c.toLowerCase().replaceAll(" ", "-")}`} onClick={() => setNewPlay({ ...newPlay, category: c })}
                        className={`text-xs px-2.5 py-1 rounded-full border ${newPlay.category === c ? "border-primary bg-primary/10" : "border-border text-muted-foreground"}`}>{CATEGORY_LABELS[c]}</button>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">Uses your team's <span className="text-foreground">Starting 15</span> in the standard GAA formation. Edit it in <span className="text-foreground">Squad</span>.</div>
                <Button data-testid="new-play-confirm" onClick={createPlay} className="w-full">Open in studio</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <Input data-testid="playbook-search" aria-label="Search plays" placeholder="Search plays, situations or coaching ideas…" value={search} onChange={(event) => setSearch(event.target.value)} className="mb-4 max-w-lg" />
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`filter-${c}`} onClick={() => setFilter(c)}
              className={`whitespace-nowrap text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full transition-colors ${filter === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{CATEGORY_LABELS[c]}</button>
          ))}
        </div>

        {shown.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-xl">
            <Clapperboard className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p data-testid="playbook-empty" className="text-muted-foreground">{search || filter !== "ALL" ? "No plays match this search or category." : "No plays yet. Create your first tactic."}</p>
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map((group) => (
              <section key={group.category} data-testid={`play-category-${group.category.toLowerCase().replaceAll(" ", "-")}`}>
                <h2 data-testid={`play-heading-${group.category.toLowerCase().replaceAll(" ", "-")}`} className="font-display font-bold text-base md:text-lg border-b border-border pb-3 mb-4">{CATEGORY_LABELS[group.category] || group.category}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {group.plays.map((p) => (
              <div key={p.id} data-testid={`play-card-${p.id}`} onClick={() => nav(`/studio/${p.id}`)}
                className="group rounded-xl border border-border bg-card overflow-hidden hover:border-primary/60 transition-colors cursor-pointer">
                <div className="relative h-32 bg-gradient-to-br from-[#0B381E] to-[#0E4424] overflow-hidden">
                  <PitchMini entities={p.entities} color={team?.colorPrimary} />
                  <div data-testid={`play-card-category-${p.id}`} className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider bg-background/70 backdrop-blur rounded px-2 py-0.5">{CATEGORY_LABELS[p.category] || p.category}</div>
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[11px] font-mono2 bg-background/70 rounded px-1.5 py-0.5"><Clock className="h-3 w-3" />{p.duration}s</div>
                  {p.sourcePack && <div data-testid={`play-format-${p.id}`} className="absolute bottom-2 left-2 bg-background/80 rounded px-1.5 py-0.5 text-[10px] text-primary font-semibold">15 v 15 · Animated</div>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <h3 data-testid={`play-title-${p.id}`} className="font-display font-bold leading-tight">{p.scenarioCode && p.name.startsWith(`${p.scenarioCode} · `) ? p.name.slice(p.scenarioCode.length + 3) : p.name}</h3>
                    <button data-testid={`duplicate-play-${p.id}`} onClick={(e) => duplicatePlay(p, e)} title="Create editable copy" aria-label={`Duplicate ${p.name}`} className="shrink-0 ml-auto h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Copy className="h-4 w-4" /></button>
                    <button data-testid={`delete-play-${p.id}`} onClick={(e) => del(p, e)} title="Delete play" className="shrink-0 -mr-1 -mt-1 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 min-h-[2rem]">{p.description || "No description yet."}</p>
                </div>
              </div>
            ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      {boardOpen && team && (
        <Whiteboard play={{ entities: buildEntities(team), annotations: [] }} teamColors={{ primary: team.colorPrimary || "#0EA5E9" }} jersey={team.jersey} onClose={() => setBoardOpen(false)} />
      )}
      <DeletePlayDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }} playName={pendingDelete?.name} onConfirm={confirmDelete} busy={deleting} />
    </div>
  );
}

function PitchMini({ entities = [], color }) {
  return (
    <svg viewBox="0 0 90 60" className="absolute inset-0 h-full w-full opacity-70">
      <rect x="4" y="4" width="82" height="52" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" />
      <line x1="4" y1="30" x2="86" y2="30" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
      <circle cx="45" cy="30" r="6" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.5" />
      {entities.map((entity) => <circle key={entity.id} cx={4 + (90 - entity.x) / 90 * 82} cy={4 + (145 - entity.y) / 145 * 52} r="1.3" fill={entity.team === "A" ? color : "#f8fafc"} stroke="#07120a" strokeWidth="0.3" />)}
    </svg>
  );
}
